// components/admin/OutOfStockDialog.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Box,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Snackbar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Inventory as InventoryIcon,
  Category as CategoryIcon,
  MoreVert as MoreVertIcon,
  RestartAlt as RestockIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../services/api';

const OutOfStockDialog = ({ open, onClose, onRestockComplete }) => {
  const [products, setProducts] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({
    totalOutOfStock: 0,
    totalLowStock: 0,
    totalProducts: 0,
    categoriesWithOutOfStock: 0
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [restocking, setRestocking] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [restockQuantity, setRestockQuantity] = useState({});
  const [bulkRestockQuantity, setBulkRestockQuantity] = useState(10);

  // Fetch out of stock products
  const fetchOutOfStockProducts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/products/out-of-stock', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          category: selectedCategory,
          search: searchQuery
        }
      });
      
      setProducts(response.data.products);
      setLowStockProducts(response.data.lowStockProducts);
      setStats(response.data.stats);
      setTotalItems(response.data.pagination.totalItems);
    } catch (error) {
      console.error('Error fetching out of stock products:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch out of stock products',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, selectedCategory, searchQuery]);

  // Fetch categories for filter
  const fetchCategories = useCallback(async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchOutOfStockProducts();
      fetchCategories();
    }
  }, [open, fetchOutOfStockProducts, fetchCategories]);

  // Handle search
  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  // Handle category filter
  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
    setPage(0);
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Handle restock single product
  const handleRestockProduct = async (productId, quantity) => {
    setRestocking(true);
    try {
      await api.post('/admin/products/restock', {
        products: [{ productId, quantity }]
      });
      
      setSnackbar({
        open: true,
        message: `Successfully restocked ${quantity} items`,
        severity: 'success'
      });
      
      fetchOutOfStockProducts();
      if (onRestockComplete) onRestockComplete();
    } catch (error) {
      console.error('Error restocking product:', error);
      setSnackbar({
        open: true,
        message: 'Failed to restock product',
        severity: 'error'
      });
    } finally {
      setRestocking(false);
      handleMenuClose();
    }
  };

  // Handle bulk restock
  const handleBulkRestock = async () => {
    if (selectedProducts.size === 0) {
      setSnackbar({
        open: true,
        message: 'No products selected',
        severity: 'warning'
      });
      return;
    }
    
    setRestocking(true);
    try {
      const productsToRestock = Array.from(selectedProducts).map(productId => ({
        productId,
        quantity: restockQuantity[productId] || bulkRestockQuantity
      }));
      
      const response = await api.post('/admin/products/restock', {
        products: productsToRestock
      });
      
      setSnackbar({
        open: true,
        message: response.data.message,
        severity: 'success'
      });
      
      setSelectedProducts(new Set());
      fetchOutOfStockProducts();
      if (onRestockComplete) onRestockComplete();
    } catch (error) {
      console.error('Error bulk restocking:', error);
      setSnackbar({
        open: true,
        message: 'Failed to bulk restock products',
        severity: 'error'
      });
    } finally {
      setRestocking(false);
    }
  };

  // Handle select product for bulk restock
  const handleSelectProduct = (productId) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectedProducts.size === products.length) {
      setSelectedProducts(new Set());
    } else {
      const allIds = products.map(p => p.id);
      setSelectedProducts(new Set(allIds));
    }
  };

  // Menu handlers
  const handleMenuOpen = (event, product) => {
    setAnchorEl(event.currentTarget);
    setSelectedProduct(product);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedProduct(null);
  };

  // Export to CSV
  const handleExportCSV = () => {
    const exportData = products.map(product => ({
      'Product ID': product.id,
      'Product Name': product.name,
      'Product Name (EN)': product.name_en,
      'Category': product.category?.name || 'N/A',
      'Quantity': product.quantity,
      'Price': product.price,
      'Last Updated': new Date(product.updatedAt).toLocaleString()
    }));
    
    const csv = convertToCSV(exportData);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `out-of-stock-products-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const convertToCSV = (data) => {
    const headers = Object.keys(data[0]);
    const rows = data.map(obj => headers.map(header => JSON.stringify(obj[header] || '')).join(','));
    return [headers.join(','), ...rows].join('\n');
  };

  // Status chip component
  const StatusChip = ({ quantity }) => {
    if (quantity <= 0) {
      return <Chip label="Out of Stock" size="small" color="error" icon={<WarningIcon />} />;
    } else if (quantity <= 10) {
      return <Chip label={`Low Stock (${quantity})`} size="small" color="warning" />;
    }
    return <Chip label={`In Stock (${quantity})`} size="small" color="success" />;
  };

  // Current products based on tab
  const currentProducts = tabValue === 0 ? products : lowStockProducts;
  const currentTitle = tabValue === 0 ? 'Out of Stock Products' : 'Low Stock Products';

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#0D0D0D',
            borderRadius: '20px',
            minHeight: '80vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(255,215,0,0.15)', pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#FFD700' }}>
                Inventory Management
              </Typography>
              <Typography variant="body2" sx={{ color: '#888888', mt: 0.5 }}>
                Manage out of stock and low stock products
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Export to CSV">
                <IconButton onClick={handleExportCSV} sx={{ color: '#FFD700' }}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Refresh">
                <IconButton onClick={fetchOutOfStockProducts} sx={{ color: '#FFD700' }}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <IconButton onClick={onClose} sx={{ color: '#888888' }}>
                ✕
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {/* Statistics Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ backgroundColor: '#1A1A1A', border: '1px solid #2D2D2D' }}>
                <CardContent>
                  <Typography variant="body2" color="#888888">Total Out of Stock</Typography>
                  <Typography variant="h4" sx={{ color: '#FF4444', fontWeight: 800 }}>
                    {stats.totalOutOfStock}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ backgroundColor: '#1A1A1A', border: '1px solid #2D2D2D' }}>
                <CardContent>
                  <Typography variant="body2" color="#888888">Low Stock (≤10)</Typography>
                  <Typography variant="h4" sx={{ color: '#FFA500', fontWeight: 800 }}>
                    {stats.totalLowStock}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ backgroundColor: '#1A1A1A', border: '1px solid #2D2D2D' }}>
                <CardContent>
                  <Typography variant="body2" color="#888888">Total Products</Typography>
                  <Typography variant="h4" sx={{ color: '#4CAF50', fontWeight: 800 }}>
                    {stats.totalProducts}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ backgroundColor: '#1A1A1A', border: '1px solid #2D2D2D' }}>
                <CardContent>
                  <Typography variant="body2" color="#888888">Categories Affected</Typography>
                  <Typography variant="h4" sx={{ color: '#FFD700', fontWeight: 800 }}>
                    {stats.categoriesWithOutOfStock}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Filters */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search products..."
              value={searchQuery}
              onChange={handleSearch}
              sx={{ flex: 1, minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#888888' }} />
                  </InputAdornment>
                ),
                sx: { backgroundColor: '#1A1A1A', borderRadius: '10px' }
              }}
            />
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategory}
                onChange={handleCategoryChange}
                label="Category"
                sx={{ backgroundColor: '#1A1A1A', borderRadius: '10px' }}
              >
                <MenuItem value="all">All Categories</MenuItem>
                {categories.map(cat => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {tabValue === 0 && selectedProducts.size > 0 && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  size="small"
                  type="number"
                  placeholder="Quantity"
                  value={bulkRestockQuantity}
                  onChange={(e) => setBulkRestockQuantity(parseInt(e.target.value) || 0)}
                  sx={{ width: 100 }}
                  InputProps={{ sx: { backgroundColor: '#1A1A1A' } }}
                />
                <Button
                  variant="contained"
                  startIcon={<RestockIcon />}
                  onClick={handleBulkRestock}
                  disabled={restocking}
                  sx={{
                    backgroundColor: '#FFD700',
                    color: '#000000',
                    '&:hover': { backgroundColor: '#FFE55C' }
                  }}
                >
                  Bulk Restock ({selectedProducts.size})
                </Button>
              </Box>
            )}
          </Box>

          {/* Tabs */}
          <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2, borderBottom: '1px solid #2D2D2D' }}>
            <Tab label={`Out of Stock (${stats.totalOutOfStock})`} />
            <Tab label={`Low Stock (${stats.totalLowStock})`} />
          </Tabs>

          {/* Products Table */}
          <TableContainer component={Paper} sx={{ backgroundColor: '#1A1A1A' }}>
            <Table>
              <TableHead>
                <TableRow>
                  {tabValue === 0 && (
                    <TableCell padding="checkbox">
                      <input
                        type="checkbox"
                        checked={selectedProducts.size === products.length && products.length > 0}
                        onChange={handleSelectAll}
                        style={{ cursor: 'pointer' }}
                      />
                    </TableCell>
                  )}
                  <TableCell>Product</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="center">Quantity</TableCell>
                  <TableCell align="center">Price (MYR)</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={tabValue === 0 ? 7 : 6} align="center" sx={{ py: 8 }}>
                      <CircularProgress sx={{ color: '#FFD700' }} />
                    </TableCell>
                  </TableRow>
                ) : currentProducts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={tabValue === 0 ? 7 : 6} align="center" sx={{ py: 8 }}>
                      <CheckCircleIcon sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                      <Typography color="#888888">
                        No {tabValue === 0 ? 'out of stock' : 'low stock'} products found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  currentProducts.map((product) => (
                    <TableRow key={product.id} hover>
                      {tabValue === 0 && (
                        <TableCell padding="checkbox">
                          <input
                            type="checkbox"
                            checked={selectedProducts.has(product.id)}
                            onChange={() => handleSelectProduct(product.id)}
                            style={{ cursor: 'pointer' }}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {product.name}
                          </Typography>
                          <Typography variant="caption" color="#888888">
                            ID: {product.id}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={product.category?.name || 'Uncategorized'}
                          sx={{ backgroundColor: '#2D2D2D', color: '#FFFFFF' }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography sx={{ 
                          color: product.quantity <= 0 ? '#FF4444' : '#FFA500',
                          fontWeight: 600
                        }}>
                          {product.quantity}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{product.price.toFixed(2)}</TableCell>
                      <TableCell align="center">
                        <StatusChip quantity={product.quantity} />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, product)}>
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalItems}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 20, 50, 100]}
            sx={{ color: '#888888', mt: 2 }}
          />
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid rgba(255,215,0,0.15)', p: 2 }}>
          <Button onClick={onClose} sx={{ color: '#888888' }}>
            Close
          </Button>
          {tabValue === 0 && selectedProducts.size > 0 && (
            <Button
              variant="contained"
              onClick={handleBulkRestock}
              disabled={restocking}
              sx={{
                backgroundColor: '#FFD700',
                color: '#000000',
                '&:hover': { backgroundColor: '#FFE55C' }
              }}
            >
              {restocking ? <CircularProgress size={20} /> : `Restock Selected (${selectedProducts.size})`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Restock Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{ sx: { backgroundColor: '#1A1A1A', border: '1px solid #2D2D2D' } }}
      >
        <MenuItem>
          <TextField
            type="number"
            size="small"
            placeholder="Quantity"
            fullWidth
            autoFocus
            onChange={(e) => setRestockQuantity({ ...restockQuantity, [selectedProduct?.id]: parseInt(e.target.value) || 0 })}
            sx={{ mr: 1 }}
          />
          <Button
            size="small"
            onClick={() => handleRestockProduct(selectedProduct?.id, restockQuantity[selectedProduct?.id] || 10)}
            sx={{ color: '#FFD700' }}
          >
            Restock
          </Button>
        </MenuItem>
      </Menu>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ backgroundColor: '#0D0D0D', color: '#FFFFFF' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default OutOfStockDialog;